import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/destinations", label: "Destinations" },
  { to: "/journeys", label: "Journeys" },
  { to: "/about", label: "Our Approach" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hero on these routes is dark-image, so navbar starts transparent w/ ivory text
  const heroRoutes = ["/"];
  const onHeroRoute = heroRoutes.includes(pathname);
  const transparent = onHeroRoute && !scrolled;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        transparent ? "bg-transparent" : "bg-ivory/90 backdrop-blur-xl border-b border-mist/40"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-baseline gap-1">
          <span className={`font-serif text-2xl tracking-tight ${transparent ? "text-ivory" : "text-jade"}`}>Abbot</span>
          <span className={`font-serif text-2xl italic tracking-tight ${transparent ? "text-copper" : "text-copper"}`}>Asia</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `text-[12px] tracking-[0.18em] uppercase font-medium transition-colors ${
                  transparent ? "text-ivory/90 hover:text-ivory" : "text-ink/80 hover:text-jade"
                } ${isActive ? "!text-copper" : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          {user ? (
            <Link
              to="/portal"
              data-testid="nav-portal"
              className={`text-[12px] tracking-[0.18em] uppercase font-medium ${
                transparent ? "text-ivory" : "text-jade"
              }`}
            >
              My Journey
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className={`text-[12px] tracking-[0.18em] uppercase font-medium ${
                transparent ? "text-ivory/90 hover:text-ivory" : "text-ink/80 hover:text-jade"
              }`}
            >
              Client Login
            </Link>
          )}
          <Link to="/contact" data-testid="nav-cta-start-trip" className="btn-primary text-[11px]">
            Start Your Custom Trip
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Open menu"
        >
          {open ? <X className={transparent ? "text-ivory" : "text-jade"} /> : <Menu className={transparent ? "text-ivory" : "text-jade"} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-ivory border-t border-mist/40">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setOpen(false)}
                className="text-[13px] tracking-[0.18em] uppercase font-medium text-ink/80"
              >
                {item.label}
              </Link>
            ))}
            <div className="divider-thin" />
            {user ? (
              <Link to="/portal" onClick={() => setOpen(false)} className="text-[13px] tracking-[0.18em] uppercase font-medium text-jade">My Journey</Link>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="text-[13px] tracking-[0.18em] uppercase font-medium text-jade">Client Login</Link>
            )}
            <Link to="/contact" onClick={() => setOpen(false)} className="btn-primary text-[11px]">Start Your Custom Trip</Link>
          </div>
        </div>
      )}
    </header>
  );
}
